import * as Sentry from "@sentry/react";

import {
  useCompletedFormState,
  useCoveragePriceCalculations,
  useFormState,
} from "Store/priceCalculation/priceCalculationHooks";

import { MarkdownWrap, Paragraph } from "App/global-style";
import { ProductPriceTile } from "Components/Features/CheckPrice/Tiles/ProductPriceTile";
import { DittoCoverageCard } from "Components/Features/CheckPrice/Tiles/ProductPriceTile/Coverages/DittoCoverages/DittoCoverageCard";
import { StyledComparisonWrap } from "Components/Features/CheckPrice/Tiles/ProductPriceTile/productPriceTile.styles";
import { SystemErrorAlert } from "Components/Form/Alert/SystemErrorAlert";
import { NumericInput } from "Components/Form/Input";
import { InputMode } from "Components/Form/Input/inputTypes";
import { InputFailureTracker } from "Components/InputFailureTracker";
import { NavigationButtons } from "Components/NavigationButtons";
import { VALUE_TYPE } from "Components/NumberValue";
import {
  getCollectionOfDittoComponents,
  useTexts,
} from "Components/TextContext";
import { TextComponentNameLevel1 } from "Components/TextContext/textConstants";
import { CheckPriceProps } from "Features/CheckPrice";
import { SopSicknessLeaveProductIntroFormValues } from "Features/CheckPrice/Products/LifeProducts/SopSicknessLeave/ProductIntro/sopSicknessLeaveProductIntroInterfaces";
import { validationSchema } from "Features/CheckPrice/Products/LifeProducts/SopSicknessLeave/ProductPrice/SopSicknessLeaveProductPriceValidation";
import { MAX_SUM_DAILY_PAYOUT_AMOUNT } from "Features/CheckPrice/Products/LifeProducts/SopSicknessLeave/sopSicknessLeaveConstants";
import {
  SopSicknessLeavePriceRequestInfo,
  WaitingPeriodBeforePayoutStarts,
} from "Features/CheckPrice/Products/LifeProducts/SopSicknessLeave/sopSicknessLeaveInterfaces";
import { getCustomerAge } from "Features/CheckPrice/Products/LifeProducts/SopSicknessLeave/sopSicknessLeaveUtils";
import { BackendProductInfo } from "Features/CheckPrice/Products/productConstants";
import { ProductBackendName } from "Features/CheckPrice/Products/productTypes";
import { useAddProductToCartHandler } from "Store/addProductToCart/hooks";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import { SopSicknessLeaveCoverage } from "Store/interfaces/products/coverage";
import { updatePriceCalculationFormState } from "Store/priceCalculation/priceCalculationSlice";
import { ReduxState } from "Store/rootReducer";
import { useCustomerDateOfBirth } from "Store/userIdentification/userIdentificationHooks";
import { getError } from "Utils/productUtils";
import { useUnleashFeatureFlag } from "Utils/unleash";
import { FeatureFlagTask } from "Utils/unleash/flags";
import { Form, FormikProps } from "formik";
import React from "react";
import ReactMarkdown from "react-markdown";
import { useDispatch, useSelector } from "react-redux";
import { useDebouncedCallback } from "use-debounce";
import {
  SOP_SICKNESS_LEAVE_ABOUT_YOU_STEP,
  SOP_SICKNESS_LEAVE_PRICE_STEP,
} from "..";
import { SopSlider } from "./SOPSlider";
import { SopSicknessLeaveProductPriceFormValues } from "./sopSicknessLeaveProductPriceFormValues";

export const SopSicknessLeaveProductPrice = ({
  updatePriceRequest,
}: CheckPriceProps): React.ReactElement => {
  const { getText } = useTexts();
  const handleAddProductToCart = useAddProductToCartHandler(
    SOP_SICKNESS_LEAVE_PRICE_STEP.id,
  );
  const coverage = SopSicknessLeaveCoverage.SOP_SICKNESS_LEAVE;
  const showSOPSlider = useUnleashFeatureFlag(
    FeatureFlagTask.SSX_2245_SOP_CALCULATOR,
  );

  const introFormValues =
    useCompletedFormState<SopSicknessLeaveProductIntroFormValues>(
      SOP_SICKNESS_LEAVE_ABOUT_YOU_STEP.id,
    );

  const initialFormState = useFormState(SOP_SICKNESS_LEAVE_PRICE_STEP.id, {
    coverage: coverage,
    dailyPayoutAmount: introFormValues.dailyPayoutAmount,
  });

  const values = {
    ...initialFormState,
    dailyPayoutAmount: introFormValues.dailyPayoutAmount,
  };

  const customerDateOfBirth = useCustomerDateOfBirth();
  const age = getCustomerAge(customerDateOfBirth);

  const dispatch = useDispatch();
  const { priceCalculation, addProductToCart } = useSelector(
    (state: ReduxState) => state,
  );

  const [coverages] = useCoveragePriceCalculations();

  const { sopsicknessleave: productCardTextComponents } =
    getCollectionOfDittoComponents(TextComponentNameLevel1.COVERAGE_CARD);

  const handleSubmit = (
    formProps: SopSicknessLeaveProductPriceFormValues,
  ): void => {
    const productToAdd = {
      coverage: formProps.coverage,
      productType: INSURANCE_NAME_VALUE.SOP_SICKNESS_LEAVE,
      dailyPayoutAmount: formProps.dailyPayoutAmount,
    };

    handleAddProductToCart(formProps, [productToAdd]);
  };

  const recalculatePrice = useDebouncedCallback((newAmount: number) => {
    dispatch(
      updatePriceCalculationFormState({
        stepId: SOP_SICKNESS_LEAVE_PRICE_STEP.id,
        formState: {
          coverage,
          dailyPayoutAmount: newAmount,
        },
      }),
    );

    dispatch(
      updatePriceCalculationFormState({
        stepId: SOP_SICKNESS_LEAVE_ABOUT_YOU_STEP.id,
        formState: {
          ...introFormValues,
          dailyPayoutAmount: newAmount,
        },
      }),
    );

    updatePriceRequest({
      productPriceUrl: BackendProductInfo.SOP_SICKNESS_LEAVE.priceReqUrl,
      stepForwardOnSuccess: false,
      updatedProductPriceRequestInfo: {
        age: age,
        dailyPayoutAmount: newAmount,
        waitingPeriodBeforePayoutStarts:
          WaitingPeriodBeforePayoutStarts.SIXTEEN_DAYS,
      },
    });
  }, 500);

  return (
    <Sentry.ErrorBoundary
      beforeCapture={(scope) => {
        scope.setTag(
          "location",
          `CheckPrice - ProductPrice - ${INSURANCE_NAME_VALUE.SOP_SICKNESS_LEAVE}`,
        );
      }}
    >
      <ProductPriceTile
        initialValues={values}
        onSubmit={handleSubmit}
        header={SOP_SICKNESS_LEAVE_PRICE_STEP.title}
        validationSchema={validationSchema()}
      >
        {(props: FormikProps<SopSicknessLeavePriceRequestInfo>) => (
          <Form>
            <Paragraph>
              {getText("sopsicknessleaveform.ingress.checkpricestepsubheading")}
            </Paragraph>
            <NumericInput
              name="dailyPayoutAmount"
              id="dailyPayoutAmount"
              label={getText("sopsicknessleaveform.label.dailypayoutamount")}
              hint={getText(
                "sopsicknessleaveform.hint.dailypayoutamountslider",
              )}
              error={getError("dailyPayoutAmount", props)}
              inputMode={InputMode.NUMERIC}
              inputProps={{
                autoComplete: "off",
                maxLength: 4,
              }}
              max={MAX_SUM_DAILY_PAYOUT_AMOUNT}
              min={1}
              onChange={(newDailyAmount) => {
                recalculatePrice(Number(newDailyAmount.value));
              }}
              suffix={VALUE_TYPE.NOK}
            />
            {showSOPSlider && (
              <SopSlider
                props={props}
                currentSliderValue={(value) => {
                  props.setFieldValue("dailyPayoutAmount", value);
                }}
                handleCustomerChange={(newDailyAmount: number) => {
                  props.setFieldValue("dailyPayoutAmount", newDailyAmount);
                  recalculatePrice(newDailyAmount);
                }}
              />
            )}
            <DittoCoverageCard
              key={coverage}
              fieldName="coverage"
              product={ProductBackendName.SOP_SICKNESS_LEAVE}
              coverage={coverage}
              textComponents={productCardTextComponents}
              prices={coverages[1].prices}
              priceIsLoading={priceCalculation.priceCalculationResult.isLoading}
              hideRadioButton
            >
              <MarkdownWrap>
                <ReactMarkdown>
                  {getText("sopsicknessleaveform.ingress.checkprice")}
                </ReactMarkdown>
              </MarkdownWrap>
            </DittoCoverageCard>

            {addProductToCart.error && (
              <SystemErrorAlert alertId="addProductToCart" marginTop="32px" />
            )}
            <StyledComparisonWrap>
              <NavigationButtons isLoading={addProductToCart.isLoading} />
            </StyledComparisonWrap>
            <InputFailureTracker
              productName={INSURANCE_NAME_VALUE.SOP_SICKNESS_LEAVE}
              stepId={SOP_SICKNESS_LEAVE_PRICE_STEP.id}
            />
          </Form>
        )}
      </ProductPriceTile>
    </Sentry.ErrorBoundary>
  );
};
